import React from 'react';
import {
  FieldLabel,
  Row,
  RowWrapper,
  Section,
  SectionTitle,
  UserFormProps,
  getInputField,
  FieldContainer,
} from '../../../components/AdminFormComponents';
import { Tooltip } from 'react-covideo-common';
import { MdInfo } from 'react-icons/md';
import { theme } from 'lib/style';
import CountryCodePicker from 'lib/components/country-code-picker/CountryCodePicker';

import { Field, FieldProps } from 'formik';
import { TextInput } from 'lib/components';
import {
  combinePhone,
  combinePhoneFull,
  CountryCode,
  extractExtension,
  formatLocalNumber,
  parseCountryFromPhone,
  parseLocalNumber,
  stripExtension,
} from '../../contactHelper';
import { RESELLER_IDS } from 'lib/const';

export const ContactSection = (props: UserFormProps) => {
  const { isSubmitting, isNewUser, hasEmail2Field, values, setFieldValue } =
    props;

  const [phone1Country, setPhone1Country] = React.useState(
    parseCountryFromPhone(values.phone1 || '') as CountryCode
  );
  const [phone1Local, setPhone1Local] = React.useState(
    parseLocalNumber(stripExtension(values.phone1 || ''))
  );
  const [phone2Country, setPhone2Country] = React.useState(
    parseCountryFromPhone(values.phone2 || '') as CountryCode
  );
  const [phone2Local, setPhone2Local] = React.useState(
    parseLocalNumber(stripExtension(values.phone2 || ''))
  );
  const [cellPhoneCountry, setCellPhoneCountry] = React.useState(
    parseCountryFromPhone(values.cellPhone || '') as CountryCode
  );
  const [cellPhoneLocal, setCellPhoneLocal] = React.useState(
    parseLocalNumber(values.cellPhone || '')
  );

  const [phone1Ext, setPhone1Ext] = React.useState(
    extractExtension(values.phone1 || '')
  );
  const [phone2Ext, setPhone2Ext] = React.useState(
    extractExtension(values.phone2 || '')
  );

  const isCurrentCDKReseller =
    props.values.resellerId?.toString() === RESELLER_IDS.CDK;
  const handleCountryChange = (
    phoneField: 'phone1' | 'phone2' | 'cellPhone',
    newCountry: string
  ) => {
    switch (phoneField) {
      case 'phone1':
        setPhone1Country(newCountry as CountryCode);
        setFieldValue(
          'phone1',
          combinePhoneFull(newCountry as CountryCode, phone1Local, phone1Ext)
        );
        setPhone2Country(newCountry as CountryCode);
        setFieldValue(
          'phone2',
          combinePhoneFull(newCountry as CountryCode, phone2Local, phone2Ext)
        );
        setCellPhoneCountry(newCountry as CountryCode);
        setFieldValue(
          'cellPhone',
          combinePhone(newCountry as CountryCode, cellPhoneLocal)
        );
        break;
      case 'phone2':
        setPhone2Country(newCountry as CountryCode);
        setFieldValue(
          'phone2',
          combinePhoneFull(newCountry as CountryCode, phone2Local, phone2Ext)
        );
        break;
      case 'cellPhone':
        setCellPhoneCountry(newCountry as CountryCode);
        setFieldValue(
          'cellPhone',
          combinePhone(newCountry as CountryCode, cellPhoneLocal)
        );
        break;
      default:
        break;
    }
  };

  const handleLocalChange = (
    phoneField: 'phone1' | 'phone2' | 'cellPhone',
    newLocal: string
  ) => {
    const valid = /^[0-9\s\-().]*$/.test(newLocal);
    if (!valid) {
      return;
    }
    switch (phoneField) {
      case 'phone1': {
        const formattedLocal = formatLocalNumber(phone1Country, newLocal);
        setPhone1Local(formattedLocal);
        setFieldValue(
          'phone1',
          combinePhoneFull(phone1Country, formattedLocal, phone1Ext)
        );
        break;
      }
      case 'phone2': {
        const formattedLocal = formatLocalNumber(phone2Country, newLocal);
        setPhone2Local(formattedLocal);
        setFieldValue(
          'phone2',
          combinePhoneFull(phone2Country, formattedLocal, phone2Ext)
        );
        break;
      }
      case 'cellPhone': {
        const formattedLocal = formatLocalNumber(cellPhoneCountry, newLocal);
        setCellPhoneLocal(formattedLocal);
        setFieldValue(
          'cellPhone',
          combinePhone(cellPhoneCountry, formattedLocal)
        );
        break;
      }
      default:
        break;
    }
  };

  const handleExtensionChange = (
    phoneField: 'phone1' | 'phone2',
    newExt: string
  ) => {
    switch (phoneField) {
      case 'phone1':
        setPhone1Ext(newExt);
        setFieldValue(
          'phone1',
          combinePhoneFull(phone1Country, phone1Local, newExt)
        );
        break;
      case 'phone2':
        setPhone2Ext(newExt);
        setFieldValue(
          'phone2',
          combinePhoneFull(phone2Country, phone2Local, newExt)
        );
        break;
    }
  };

  return (
    <Section id={'contactInfo'}>
      <SectionTitle>Contact</SectionTitle>
      <RowWrapper>
        {!isNewUser && (
          <Row>
            <FieldLabel>Email</FieldLabel>
            {getInputField(
              'email',
              true,
              false,
              isSubmitting,
              'email',
              isCurrentCDKReseller
            )}
          </Row>
        )}
        {!isNewUser && hasEmail2Field && (
          <Row>
            <FieldLabel>Email 2</FieldLabel>
            {getInputField('email2', false, false, isSubmitting, 'email')}
          </Row>
        )}
        <Row>
          <FieldLabel>Phone 1</FieldLabel>
          <Field type={'text'} autoComplete='off'>
            {({ field }: FieldProps) => {
              return (
                <FieldContainer gap={12} flexDirection='row'>
                  <CountryCodePicker
                    ext={phone1Country}
                    setExtension={newCountry =>
                      handleCountryChange('phone1', newCountry)
                    }
                    disabled={isSubmitting || isCurrentCDKReseller}
                  />
                  <TextInput
                    {...field}
                    type='tel'
                    inputMode='numeric'
                    pattern='[0-9\s\-().]*'
                    value={phone1Local}
                    disabled={isSubmitting || isCurrentCDKReseller}
                    onChange={e => handleLocalChange('phone1', e.target.value)}
                  />
                  <TextInput
                    value={phone1Ext}
                    disabled={isSubmitting || isCurrentCDKReseller}
                    placeholder='Ext (optional)'
                    onChange={e =>
                      handleExtensionChange('phone1', e.target.value)
                    }
                  />
                </FieldContainer>
              );
            }}
          </Field>
        </Row>
        <Row>
          <FieldLabel>Phone 2</FieldLabel>
          <Field type={'text'} autoComplete='off'>
            {({ field }: FieldProps) => {
              return (
                <FieldContainer gap={12} flexDirection='row'>
                  <CountryCodePicker
                    ext={phone2Country}
                    setExtension={newCountry =>
                      handleCountryChange('phone2', newCountry)
                    }
                  />
                  <TextInput
                    {...field}
                    type='tel'
                    inputMode='numeric'
                    pattern='[0-9\s\-().]*'
                    value={phone2Local}
                    disabled={isSubmitting}
                    onChange={e => handleLocalChange('phone2', e.target.value)}
                  />
                  <TextInput
                    value={phone2Ext}
                    disabled={isSubmitting}
                    placeholder='Ext (optional)'
                    onChange={e =>
                      handleExtensionChange('phone2', e.target.value)
                    }
                  />
                </FieldContainer>
              );
            }}
          </Field>
        </Row>
        <Row>
          <FieldLabel>
            Cell Phone
            <Tooltip
              popup={
                'This is useful for delivering important onboarding content to each user.'
              }
              position='right'
              extendStyles={{
                popup: {
                  width: '310px',
                  fontSize: '16px',
                  lineHeight: '24px',
                  color: theme.palette.neutral100,
                  padding: '8px 12px',
                  fontWeight: '400',
                },
              }}
            >
              <MdInfo
                style={{
                  top: '2px',
                }}
              />
            </Tooltip>
          </FieldLabel>
          <Field type={'text'} autoComplete='off'>
            {({ field }: FieldProps) => {
              return (
                <FieldContainer gap={12} flexDirection='row'>
                  <CountryCodePicker
                    ext={cellPhoneCountry}
                    setExtension={newCountry =>
                      handleCountryChange('cellPhone', newCountry)
                    }
                  />
                  <TextInput
                    {...field}
                    type='tel'
                    inputMode='numeric'
                    pattern='[0-9\s\-().]*'
                    value={cellPhoneLocal}
                    disabled={isSubmitting}
                    onChange={e =>
                      handleLocalChange('cellPhone', e.target.value)
                    }
                  />
                </FieldContainer>
              );
            }}
          </Field>
        </Row>
      </RowWrapper>
    </Section>
  );
};
